import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

/**
 * This hook is used to run a simple paginated query. We run the passed in query
 * with limit and variables and get the items and pagination data. Types are a little
 * fuzzy since we can't pass the types into the hook, so we use 'any' a bit liberally.
 */
export const usePaginatedQuery = (input: {
    query: () => void;
    queryKey: string;
    limit: number;
    queryVariables?: any;
}) => {
    const {query, queryKey, limit, queryVariables} = input;

    const {
        data,
        loading: isLoading,
        error,
        fetchMore,
        networkStatus,
        // @ts-expect-error
    } = useQuery<any, any>(query, {
        variables: {first: limit, ...queryVariables},
        fetchPolicy: 'cache-and-network',
    });

    const safeData = data || {};

    const items = safeData[queryKey];

    // @ts-expect-error
    const itemNodes: any[] = items ? items.edges.map((edge) => edge.node) : [];
    const hasNextPage = items ? items.pageInfo.hasNextPage : false;

    const fetchMoreRows = () => {
        return fetchMore({
            variables: {
                first: limit,
                after: items ? items.pageInfo.endCursor : null,
            },
            // @ts-expect-error
            updateQuery: (
                prev: any,
                newResults: {
                    fetchMoreResult: any;
                }
            ) => {
                const oldItems = prev[queryKey];
                const fetchedItems = newResults.fetchMoreResult[queryKey];

                if (!oldItems || !fetchedItems) return;

                const newItems = {
                    ...oldItems,
                    pageInfo: fetchedItems.pageInfo,
                    edges: oldItems.edges.concat(fetchedItems.edges),
                } as const;

                return {[queryKey]: newItems};
            },
        });
    };

    return {
        items: itemNodes,
        isLoading,
        isFirstLoad: isLoading && networkStatus !== 3,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        hasNextPage,
        fetchMoreRows,
    };
};
